'use client';

import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { InstallmentValueTilesContainer } from '../../../atoms/InstallmentValueTilesContainer/InstallmentValueTilesContainer.client';
import { PaymentPause } from '../../../atoms/PaymentPause/PaymentPause.client';
import { LegalHints } from '../../../atoms/LegalHints/LegalHints.client';
import { InstallmentPriceBox } from '../../../atoms/InstallmentPriceBox/InstallmentPriceBox.client';
import { PaymentPlan } from '../../../atoms/PaymentPlan/PaymentPlan.client';
import type { InstallmentCalulatorLayoutComponentsDefaultProps } from '../types';
import { Slider } from '../../../atoms/Slider/Slider.client';
import { InfoCallToAction } from '../../../atoms/InfoCallToAction/InfoCallToAction.client';
import { LegalHintsImage } from '../../../atoms/LegalHintsImage/LegalHintsImage.client';

export type CompactProps = InstallmentCalulatorLayoutComponentsDefaultProps;

/**
 * Calculator component
 *
 * component to display the fixed installment calculator
 *
 * */
export const Compact = ({
  calculationResult,
  calculationValuta,
  isLoading,
  hasEditablePrice,
  onChangeInstallmentValue,
  onChangePaymentPauseValue,
  onChangeBasePriceValue,
}: CompactProps) => {
  const {
    currency,
    singleInstallment,
    viableInstallments,
    numberOfInstallments,
    paymentPause,
    valuta,
    supportsPaymentPause,
    type,
    minValue,
    maxValue,
  } = calculationResult;
  const {
    installmentCalculator: { hasLegalHints, legalHintsImage, hasInfoCallToAction },
  } = useConfig();

  return (
    <div className="mt-0 mb-4 flex flex-col flex-wrap gap-1.5">
      <div className="mx-0">
        <InstallmentPriceBox
          currency={currency}
          calculationValuta={calculationValuta}
          singleInstallment={singleInstallment}
          isLoading={isLoading}
          hasEditablePrice={hasEditablePrice}
          onChangeBasePriceValue={onChangeBasePriceValue}
          minValue={minValue}
          maxValue={maxValue}
        />
        <div className="px-1.5 pt-1.5">
          {hasInfoCallToAction && <InfoCallToAction />}
          {type === 'FIX' && viableInstallments ? (
            <InstallmentValueTilesContainer
              installmentsList={viableInstallments}
              activeInstallment={numberOfInstallments}
              installmentSelectedHandler={onChangeInstallmentValue}
            />
          ) : (
            <Slider
              viableInstallments={viableInstallments}
              numberOfInstallments={numberOfInstallments}
              installmentSelectedHandler={onChangeInstallmentValue}
            />
          )}
          <Divider className="mt-3 mb-1.5" />
          {supportsPaymentPause && (
            <PaymentPause checked={paymentPause} checkHandler={onChangePaymentPauseValue} />
          )}
        </div>
      </div>
      <div className="mx-2 flex flex-col gap-3">
        <PaymentPlan asBox={false} calculationResult={calculationResult} />
        {hasLegalHints && (
          <div className="mt-3">
            <LegalHints paymentPauseValuta={valuta} />
          </div>
        )}
      </div>
      {legalHintsImage !== null && <LegalHintsImage />}
    </div>
  );
};
